import axios, { AxiosResponse } from "axios";
import { PropertyResource } from "../model/propertyResource";
import moment, { Moment } from "moment-timezone";
import { PropertyAccount, TimeSlotsRequest, AccountDetail, TimeSlot, TOUR, SELF_GUIDED_TOUR, VIRTUAL_TOUR } from "./types";
import d from "debug";
import { ca } from "date-fns/locale";

const debug = d("PropertyService");

const NEIGHBORHOOD = "Neighborhood";
const CITY = "City";
const REGION = "Region";
const ACCOUNT_URL = "./services/data/v48.0/sobjects/Account/";
const TIMESLOTS_URL = "./services/apexrest/v2/getAvailableTimeSlots";
const ALL_PROPERTIES_URL = "./services/apexrest/Properties";
const NEAR_BY_PROPERTIES_URL = "./services/apexrest/getNearByProperties";

function getPropertyResourceByName(propResources: PropertyResource[], propertyName: string): PropertyResource | undefined {
  let foundPropResource = undefined;
  propResources.forEach((p) => {
    if (p.name === propertyName) {
      foundPropResource = p;
    }
  });
  return foundPropResource;
}

function getPropertyIdByName(propResources: PropertyResource[], propertyName: string): string {
  const propResource = getPropertyResourceByName(propResources, propertyName);
  return propResource ? propResource.propertyId : "undefined";
}

export class PropertyService {
  //fields
  tourStartDate: Moment;
  numberOfWeeks: number;
  advBookHours: number;

  //constructor
  constructor(tourStartDate: Moment, numberOfWeeks: number = 2, advBookHours: number = 2) {
    this.tourStartDate = tourStartDate;
    this.numberOfWeeks = numberOfWeeks;
    this.advBookHours = advBookHours;
  }

  propertyResourceByPropertyId(propertyId: string, propResources: PropertyResource[]): PropertyResource | undefined {
    let found: PropertyResource | undefined = undefined;
    propResources.forEach((r) => {
      if (r.propertyId === propertyId) {
        found = r;
      }
    });
    return found;
  }

  /**
   * Fill in all property timeslots
   * @param propResources the property timeslots to fill
   */
  async populateTimeSlots(propResources: PropertyResource[],callingMode): Promise<PropertyResource[]> {
    console.log('populateTimeSlots called with callingMode: ' + callingMode);
    const minStartTime = moment().add(this.advBookHours, "hours"); // advanced booking time required
    const propertyNames = propResources.map((resource) => resource.name);
    const requestBody: TimeSlotsRequest = {
      callingMode: callingMode,
      startDate: this.tourStartDate.hour(0).minute(0).toDate(),
      numberOfDays: Math.max(1, this.numberOfWeeks * 7),
      propertyNames,
    };
    return axios.post(TIMESLOTS_URL, requestBody).then((response) => {
      let data = response.data as TimeSlot[];
      data = data.map((timeSlot) => {
        if (moment.tz.guess() === 'America/New_York'){
          timeSlot.startTime = moment(timeSlot.startTime).subtract(3,'hours').toDate();
          timeSlot.endTime   = moment(timeSlot.endTime).subtract(3,'hours').toDate();
        } else if (moment.tz.guess() === 'America/Chicago'){
          timeSlot.startTime=moment(timeSlot.startTime).subtract(2,'hours').toDate();
          timeSlot.endTime=moment(timeSlot.endTime).subtract(2,'hours').toDate();
        } else if (moment.tz.guess() === 'America/Denver'){
          timeSlot.startTime=moment(timeSlot.startTime).subtract(1,'hours').toDate();
          timeSlot.endTime=moment(timeSlot.endTime).subtract(1,'hours').toDate();
        } else {
          timeSlot.startTime=moment.utc(timeSlot.startTime).toDate();
          timeSlot.endTime=moment.utc(timeSlot.endTime).toDate();
        }
        //timeSlot.startTime = moment.utc(timeSlot.startTime).toDate();
        //timeSlot.endTime = moment.utc(timeSlot.endTime).toDate();
        if (minStartTime.isSameOrBefore(timeSlot.startTime)) {
          const targetResource = getPropertyResourceByName(propResources, timeSlot.propertyName);
          if (targetResource) {
            if (!targetResource.timeSlots) {
              targetResource.timeSlots = new Array<TimeSlot>();
            }
            if (timeSlot.remainingTours > 0) {
              targetResource.timeSlots.push(timeSlot);
            }
          }
        }
        return timeSlot;
      });
      return propResources;
    });
  }

  async getProperty(propertyId: string, withTimeSlots: boolean = true, callingMode: string): Promise<PropertyResource> {
    console.log("it came here..."+callingMode);
    const startTime = this.tourStartDate.hour(0).minute(0).toDate();
    const minStartTime = moment().add(this.advBookHours, "hours"); // advanced booking time required
    const numberOfDays = Math.max(1, this.numberOfWeeks * 7);

    return axios.get(`${ACCOUNT_URL}${propertyId}`).then(function (response) {
      const data = response.data as PropertyAccount;
      const propResource: PropertyResource = {
        name: data.Name,
        propertyId: data.Id,
        type: data.Type,
      };

      if (withTimeSlots) {
        const requestBody: TimeSlotsRequest = {
            callingMode: callingMode, 
          startDate: startTime,
          numberOfDays: numberOfDays,
          propertyNames: [propResource.name],
        };

        axios.post(TIMESLOTS_URL, requestBody).then((timeSlotsResponse) => {
          const slots = timeSlotsResponse.data as TimeSlot[];
          slots.forEach((ts) => {
            ts.startTime = moment.utc(ts.startTime).toDate();
            ts.endTime = moment.utc(ts.endTime).toDate();
          });
          propResource.timeSlots = slots.filter((ts) => {
            return ts.remainingTours > 0 && minStartTime.isSameOrBefore(ts.startTime);
          });
        });
      }
      return propResource;
    });
  }

  /**
   * Fill in all property details
   * @param propResources the property detail to fill
   */
  async populatePropertyDetails(propResources: PropertyResource[]): Promise<PropertyResource[]> {
    const allRequests: Promise<AxiosResponse<any>>[] = [];
    propResources.forEach((r) => {
      allRequests.push(axios.get(`${ACCOUNT_URL}${r.propertyId}`));
    });
    return axios.all(allRequests).then((responseArr) => {
      //this will be executed only when all requests are complete
      responseArr.forEach((response) => {
        const detail = response.data as AccountDetail;
        const resource = this.propertyResourceByPropertyId(detail.Id, propResources);
        // debug(`populating property detail for propId: ${detail.Id}`, resource);
        if (resource) {
          resource.type = detail.Type;
          resource.realPageId = detail.Property_External_Id__c;
        }
      });
      return propResources.filter((r) => r.type === NEIGHBORHOOD);
    });
  }

  async getAllProperties(withDetails: boolean = false, 
                         onlyNeighborhoods: boolean = false,
                         onlyCities: boolean = false): Promise<PropertyResource[]> {
    return axios
      .get(ALL_PROPERTIES_URL)
      .then((response) => {
        const data = response.data as PropertyAccount[];
        // debug("Properties Response.data", data);
        let propResources: PropertyResource[] = data.map<PropertyResource>((p) => {
          const propResource: PropertyResource = {
            name: p.Name,
            propertyId: p.Id,
            realPageId: p.Property_External_Id__c,
            type: p.attributes.type,
            accountType: p.AccountType__c,
            accountParentName: p.AccountParentName__c
          };
          return propResource;
        });
        return propResources.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      })
      .then((propResources) => {
        if (withDetails || onlyNeighborhoods || onlyCities) {
          return this.populatePropertyDetails(propResources).then((detailedResources) => {
            if (onlyNeighborhoods) {
              detailedResources = detailedResources.filter((r) => r.type === NEIGHBORHOOD);
            }
            if (onlyCities) {
              detailedResources = detailedResources.filter((r) => r.type === CITY || r.type === REGION);
            }
            return detailedResources;
          });
        } else {
          return propResources;
        }
      });
  }

  async getNearByProperties(
    callingMode: string,
    propertyName: string,
    radiusMiles: number = 10,
    sameRegion: boolean = false,
    withTimeSlots: boolean = true,
    propIdLookups: PropertyResource[]
  ): Promise<PropertyResource[]> {
    const requestBody = {
      propertyName,
      miles: radiusMiles,
      sameRegion: sameRegion ? "Y" : "",
    };
    return axios
      .post(NEAR_BY_PROPERTIES_URL, requestBody)
      .then((response) => {
        let propResources: PropertyResource[] = [];
        if (response.data instanceof Array && response.data.length > 0) {
          if (response.data[0].errorCode) {
            // has Error
            debug(response.data[0]);
          } else {
            const data = response.data as PropertyAccount[];
            propResources = data.map<PropertyResource>((p) => {
              const propResource: PropertyResource = {
                name: p.Name,
                propertyId: propIdLookups ? getPropertyIdByName(propIdLookups, p.Name) : p.Id,
                type: p.Type || NEIGHBORHOOD,
              };
              return propResource;
            });
          }
        }
        return propResources;
      })
      .then((propResources) => {
        console.log('Mode A- ' + callingMode);
        if (withTimeSlots && propResources) {
          return this.populateTimeSlots(propResources,callingMode)
        } else {
          return propResources;
        }
      });
  }
}

export default PropertyService;
